
import { 
  collection, 
  doc, 
  Firestore, 
  getDoc, 
  onSnapshot, 
  query, 
  orderBy,
  QuerySnapshot
} from 'firebase/firestore';
import { BaseDao } from './base-dao';

/**
 * DAO for handling user subscription and payment data.
 * Centralizes access to users/{userId}/subscription path.
 */
export class SubscriptionDao extends BaseDao {
  constructor(firestore: Firestore) {
    super(firestore);
  }

  /**
   * Listen to the current subscription document.
   */
  onSubscriptionChange(
    userId: string,
    callback: (data: any) => void,
    onError?: (error: Error) => void
  ) {
    const subRef = doc(this.firestore, 'users', userId, 'subscription', 'current');
    return onSnapshot(
      subRef,
      (snap) => {
        if (!snap.exists()) {
          callback(null);
          return;
        }
        const data = snap.data();
        const normalized = {
          ...data,
          currentPeriodEnd: data.currentPeriodEnd?.toDate?.() ? data.currentPeriodEnd.toDate().toISOString() : data.currentPeriodEnd,
          startDate: data.startDate?.toDate?.() ? data.startDate.toDate().toISOString() : data.startDate,
          updatedAt: data.updatedAt?.toDate?.() ? data.updatedAt.toDate().toISOString() : data.updatedAt,
        };
        callback(normalized);
      },
      (error) => onError?.(error)
    );
  }

  /**
   * Listen to payment records.
   */
  onPaymentsChange(userId: string, callback: (payments: any[]) => void) {
    const paymentsRef = doc(this.firestore, 'users', userId, 'subscription', 'payments');
    return onSnapshot(paymentsRef, (snap) => {
      if (!snap.exists()) {
        callback([]);
        return;
      }
      const data = snap.data();
      const records = data.records || {};
      const paymentsList = Object.values(records).map((p: any) => ({
        ...p,
        timestamp: p.timestamp?.toDate?.() ? p.timestamp.toDate().toISOString() : p.timestamp
      })).sort((a: any, b: any) => (b.timestamp || '').localeCompare(a.timestamp || ''));
      callback(paymentsList);
    });
  }

  /**
   * Listen to subscription history.
   */
  onHistoryChange(userId: string, callback: (history: any[]) => void) {
    const historyRef = doc(this.firestore, 'users', userId, 'subscription', 'history');
    return onSnapshot(historyRef, (snap) => {
      if (!snap.exists()) {
        callback([]);
        return;
      }
      const data = snap.data();
      const records = data.records || {};
      const historyList = Object.values(records).map((h: any) => ({
        ...h,
        updatedAt: h.updatedAt?.toDate?.() ? h.updatedAt.toDate().toISOString() : h.updatedAt
      })).sort((a: any, b: any) => (b.updatedAt || '').localeCompare(a.updatedAt || ''));
      callback(historyList);
    });
  }
}
